import { createBrowserRouter, Navigate, RouterProvider } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { SessionProvider } from "./app/session";
import { ListsProvider } from "./app/lists";
import { AppLayout } from "./routes/AppLayout";
import { SignInPage } from "./routes/SignInPage";
import { RegisterPage } from "./routes/RegisterPage";
import { JoinPage } from "./routes/JoinPage";
import { WelcomePage } from "./routes/WelcomePage";
import { ListsPage } from "./routes/ListsPage";
import { SearchPage } from "./routes/SearchPage";
import { ActivityPage } from "./routes/ActivityPage";
import { SettingsPage } from "./routes/SettingsPage";
import { CalendarPage } from "./routes/CalendarPage";
import { ChatPage } from "./routes/ChatPage";
import { WishesPage } from "./routes/WishesPage";
import { MapPage } from "./routes/MapPage";
import { SharePage } from "./routes/SharePage";
import { NotFoundPage } from "./routes/NotFoundPage";

/**
 * Kept for one-off requests that sit outside the delta — minting an invite, for
 * instance. Everything that belongs to a space flows through the sync engine
 * instead, so most screens never touch this.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A phone wakes from the pocket constantly; refetching on every focus is
      // how you burn a battery. The WebSocket delta feed (SPEC §7) is the real
      // freshness mechanism.
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30_000,
    },
  },
});

const router = createBrowserRouter([
  { path: "/signin", element: <SignInPage /> },
  { path: "/register", element: <RegisterPage /> },
  /*
   * The one question asked between signing up and the app, and it sits outside
   * `AppLayout` on purpose: a tab bar and a space switcher around it would
   * invite a poke past the question, and there is nothing behind it yet anyway.
   * It is the last step of signing up rather than the first screen of the app.
   */
  { path: "/welcome", element: <WelcomePage /> },
  /*
   * Приём ключа — рядом со входом, а не внутри приложения.
   *
   * Внутри `AppLayout` вошедшего проверяют раньше, чем отрисуется экран: гость
   * с квадратом уехал бы на `/signin`, а ключ остался бы висеть в адресе — то
   * есть ровно то, чего этот экран не допускает.
   */
  { path: "/join", element: <JoinPage /> },
  {
    path: "/",
    element: <AppLayout />,
    children: [
      { index: true, element: <Navigate to="/lists" replace /> },
      { path: "lists", element: <ListsPage /> },
      { path: "calendar", element: <CalendarPage /> },
      { path: "chat", element: <ChatPage /> },
      { path: "wishes", element: <WishesPage /> },
      { path: "map", element: <MapPage /> },
      { path: "share", element: <SharePage /> },
      { path: "search", element: <SearchPage /> },
      { path: "activity", element: <ActivityPage /> },
      { path: "settings", element: <SettingsPage /> },
    ],
  },
  { path: "*", element: <NotFoundPage /> },
]);

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <SessionProvider>
        <ListsProvider>
          <RouterProvider router={router} />
        </ListsProvider>
      </SessionProvider>
    </QueryClientProvider>
  );
}
